import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import InputField from 'components/form/input_field/InputField';
import Button from 'components/general/button/Button';

const EMAIL_REGEX = /^[-a-z0-9_+.]+@([-a-z0-9]+\.)+[a-z0-9]{2,}$/i;

interface ProfileStepProps {
  jobId: string;
  initialEmail: string;
  onNext: (email: string) => void;
}

interface CheckEmailResponse {
  valid?: boolean;
  error?: string;
}

const ERROR_MESSAGES = {
  NETWORK_ERROR: 'An error occurred. Please try again.',
  INVALID_EMAIL: 'Type a valid email (ex.: name@email.com)',
  DEFAULT_ERROR: 'An error occurred',
} as const;

const ProfileStep: React.FC<ProfileStepProps> = ({ jobId, initialEmail, onNext }) => {
  const styles = require('./Step.module.scss');

  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  const isEmailValid = useMemo(() => EMAIL_REGEX.test(email.trim()), [email]);
  const disableNext = useMemo(() => isChecking || !email.trim() || !isEmailValid, [isChecking, email, isEmailValid]);

  const getAuthenticityToken = useCallback(() => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }, []);

  const clearEmailError = useCallback(() => {
    setEmailError('');
  }, []);

  const validateEmailFormat = useCallback((emailValue: string) => {
    const normalizedEmail = emailValue.trim();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setEmailError(ERROR_MESSAGES.INVALID_EMAIL);
      return false;
    }
    clearEmailError();
    return true;
  }, [clearEmailError]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (emailError) clearEmailError();
  }, [emailError, clearEmailError]);

  const handleEmailBlur = useCallback(() => {
    validateEmailFormat(email);
  }, [email, validateEmailFormat]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === 'Go') && !disableNext) {
      e.preventDefault();
      handleNext();
    }
  }, [disableNext]);

  const handleNext = useCallback(async () => {
    if (disableNext) return;

    setIsChecking(true);
    clearEmailError();

    try {
      const response = await fetch(
        window.Routes.check_email_job_ad_quick_applications_url(jobId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getAuthenticityToken(),
          },
          body: JSON.stringify({ profile: { unconfirmed_email: email.trim() } }),
        }
      );

      const data: CheckEmailResponse = await response.json();

      setIsChecking(false);
      
      if (!response.ok) {
        setEmailError(data.error || ERROR_MESSAGES.DEFAULT_ERROR);
        return;
      }

      if (data.valid === false) {
        window.location.href = window.Routes.new_user_session_url();
        return;
      }

      if (data.valid) {
        onNext(email.trim());
      }
    } catch (error) {
      setEmailError(ERROR_MESSAGES.NETWORK_ERROR);
    }
  }, [disableNext, jobId, email, onNext, getAuthenticityToken, clearEmailError]);

  return (
    <>
      <div className={styles.form}>
        <InputField
          id="unconfirmed_email"
          name="unconfirmed_email"
          label="Email"
          placeholder="Type your email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onKeyDown={handleKeyDown}
          onBlur={handleEmailBlur}
          required
          error={emailError || undefined}
          inputRef={emailInputRef}
        />
      </div>

      <div className={styles.footer}>
        <Button
          onClick={handleNext}
          disabled={disableNext}
          size="M"
          iconRightName="chevronRight"
          className={styles.actionButton}
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default ProfileStep;
